/**
 * Units helper for getting user's preferred measurement units
 */
import { store } from '../state';
import type { Units } from './format';

/**
 * Get user's preferred units from preferences
 * Defaults to 'imperial' if not set
 */
export function getUnits(): Units {
  const state = store.getState();
  return state.user?.preferences?.filters_json?.app?.units || 'imperial';
}

// Discrete distance steps (displayed in user's units - miles or km)
const DISTANCE_STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30, 50, 75, 100, 150, 200, 250, 500, 1000, 3000, 5000];
const MILES_TO_KM = 1.60934;
const DISTANCE_UNLIMITED = 5000;

/** Distance slider range constants */
export const DISTANCE_SLIDER = {
  MIN: 0,
  MAX: DISTANCE_STEPS.length,  // Last position = "Any"
  UNLIMITED: DISTANCE_UNLIMITED,
  STEPS: DISTANCE_STEPS,
} as const;

/**
 * Convert slider position to km for storage.
 * Steps are in the user's display units (miles or km).
 * Last position = unlimited.
 */
export function sliderToKm(position: number, units: Units): number {
  if (position >= DISTANCE_STEPS.length) return DISTANCE_UNLIMITED;
  const displayValue = DISTANCE_STEPS[position];
  if (units === 'imperial') {
    return Math.min(Math.round(displayValue * MILES_TO_KM), DISTANCE_UNLIMITED);
  }
  return Math.min(displayValue, DISTANCE_UNLIMITED);
}

/**
 * Convert stored km to the nearest slider position.
 * Unlimited (5000+) maps to the "Any" position.
 */
export function kmToSlider(km: number, units: Units): number {
  if (km >= DISTANCE_UNLIMITED) return DISTANCE_STEPS.length;
  const displayValue = units === 'imperial' ? km / MILES_TO_KM : km;
  let closest = 0;
  let minDiff = Math.abs(DISTANCE_STEPS[0] - displayValue);
  for (let i = 1; i < DISTANCE_STEPS.length; i++) {
    const diff = Math.abs(DISTANCE_STEPS[i] - displayValue);
    if (diff < minDiff) {
      minDiff = diff;
      closest = i;
    }
  }
  return closest;
}

/**
 * Get display label for a slider position (e.g. "25 mi", "100 km", "Any")
 */
export function sliderToDisplayLabel(position: number, units: Units): string {
  if (position >= DISTANCE_STEPS.length) return 'Any';
  const value = DISTANCE_STEPS[position];
  const unit = units === 'imperial' ? 'mi' : 'km';
  return `${value.toLocaleString()} ${unit}`;
}
